/**
 * Services with no Artemis standard rate (Jacky, 25 October 2026: "artemis standard rate many missing" — then "I'll enter
 * the figures in Settings"). Every service a crew can be paid for is listed here until it has a rate; typing a figure adds
 * it to the rates below, and the page's own Save keeps it as a new version. A crew's own approved book still wins.
 */
import {useMemo} from 'react';
import {InstallerPriceCollection} from '@aiw/config';
import {Input} from '@aiw/ui';
import {useT} from '../lib/i18n';

type Rate = {service_id: string; rate: number; note: string};
type Body = {rates?: Rate[]} & Record<string, unknown>;

export function MissingRates({value, onChange}: {value: unknown; onChange: (v: unknown) => void}) {
  const {t, lang} = useT();
  const body = (value ?? {}) as Body;
  const rates = body.rates ?? [];
  const services = InstallerPriceCollection.defaults.services;
  // Rows typed here stay listed while being filled, so the input does not disappear on the first keystroke.
  const missing = useMemo(() => services.filter((s) => !rates.some((r) => r.service_id === s.id && r.note !== '')), [services, rates]);
  const groups = useMemo(() => {
    const out = new Map<string, typeof services>();
    for (const s of missing) { const g = lang === 'zh' ? s.group_zh : s.group_en; out.set(g, [...(out.get(g) ?? []), s]); }
    return [...out];
  }, [missing, lang]);
  const set = (id: string, text: string) => {
    const n = Number(text);
    const others = rates.filter((r) => r.service_id !== id);
    onChange({...body, rates: text.trim() === '' || !Number.isFinite(n) || n <= 0 ? others : [...others, {service_id: id, rate: n, note: ''}]});
  };
  const priced = services.filter((s) => rates.some((r) => r.service_id === s.id)).length;
  const open = services.length - priced;
  return <section className="mx-4 mt-3 rounded-md border bg-muted/20 p-3 text-sm">
    <p className="font-medium">{open ? t('rates.missing.title', {count: open, total: services.length}) : t('rates.missing.none')}</p>
    <p className="text-micro text-muted-foreground">{t('rates.missing.help', {priced, total: services.length})}</p>
    {groups.map(([group, list]) => <div key={group} className="mt-3">
      <p className="text-micro font-semibold text-muted-foreground">{group}</p>
      <div className="mt-1 grid gap-1 sm:grid-cols-2">{list.map((s) => {
        const current = rates.find((r) => r.service_id === s.id);
        return <label key={s.id} className="flex items-center justify-between gap-2 rounded border bg-card px-2 py-1">
          <span>{lang === 'zh' ? s.name_zh : s.name_en}</span>
          <span className="flex items-center gap-1 text-micro text-muted-foreground">$<Input className="h-8 w-20" inputMode="decimal" value={current ? String(current.rate) : ''} onChange={(e) => set(s.id, e.target.value)} aria-label={`${s.name_en} rate`}/>/{s.uom}</span>
        </label>;
      })}</div>
    </div>)}
  </section>;
}
